import React, { useState, useCallback } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

const COLORS = ['#f59e0b', '#3b82f6', '#ef4444', '#ec4899'];

const formatYAxis = (value) => value.toLocaleString();

const formatTooltipValue = (value) =>
  `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function RevenueChart({ chartData, fyKeys, loading, error, onReload }) {
  const [visibleYears, setVisibleYears] = useState(() => new Set(fyKeys));

  const toggleYear = useCallback((fyKey) => {
    setVisibleYears((prev) => {
      const next = new Set(prev);
      if (next.has(fyKey)) {
        next.delete(fyKey);
      } else {
        next.add(fyKey);
      }
      return next;
    });
  }, []);

  const titleRange = fyKeys.length > 0
    ? `${fyKeys[0]} – ${fyKeys[fyKeys.length - 1]}`
    : '';

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 flex items-center justify-center h-[480px]">
        <div className="flex items-center gap-3 text-gray-400">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading revenue data…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 flex flex-col items-center justify-center h-[480px] gap-3">
        <p className="text-red-500 text-sm">Failed to load revenue data: {error}</p>
        <button
          onClick={onReload}
          className="text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      {/* Chart title + reload */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700 text-center flex-1">
          {titleRange} Sales Revenue
        </h3>
        <button
          onClick={onReload}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Refresh"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
        </button>
      </div>

      {/* Custom legend */}
      <div className="flex items-center justify-center gap-5 mb-4">
        {fyKeys.map((fy, i) => {
          const active = visibleYears.has(fy);
          return (
            <button
              key={fy}
              onClick={() => toggleYear(fy)}
              className="flex items-center gap-1.5 text-xs font-medium transition-opacity"
              style={{ opacity: active ? 1 : 0.35 }}
            >
              <span
                className="inline-block w-8 h-2.5 rounded-sm"
                style={{ backgroundColor: COLORS[i % COLORS.length] }}
              />
              <span className={active ? 'text-gray-700' : 'text-gray-400 line-through'}>
                Year {fy.replace('FY', '')}
              </span>
            </button>
          );
        })}
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
          <CartesianGrid stroke="#f0f0f0" strokeDasharray="none" />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickFormatter={formatYAxis}
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            formatter={formatTooltipValue}
            contentStyle={{
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
              fontSize: '13px',
            }}
          />
          <Legend content={() => null} />
          {fyKeys.map((fy, i) => (
            <Line
              key={fy}
              type="linear"
              dataKey={fy}
              stroke={COLORS[i % COLORS.length]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
              hide={!visibleYears.has(fy)}
              name={`Year ${fy.replace('FY', '')}`}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
